/** Cinq séries : facture, devis, avoir, récurrence, paiement. */
export type NumberDateMode = "none" | "year" | "year_month" | "year_month_day";
export type NumberDocType = "invoice" | "quote" | "credit_note" | "recurring" | "payment";

export type NumberSeries = {
  prefix: string;
  date_mode: NumberDateMode;
  pad: number;
  next_number: number;
  reset_yearly: boolean;
  issued?: boolean;
};

export type CompanyNumbering = Record<NumberDocType, NumberSeries>;

export const NUMBER_DATE_MODES: { id: NumberDateMode; label: string }[] = [
  { id: "none", label: "Compteur" },
  { id: "year", label: "Année" },
  { id: "year_month", label: "Mois" },
  { id: "year_month_day", label: "Jour" },
];

export const NUMBER_PADS = [3, 4, 5, 6] as const;

const TYPES: NumberDocType[] = ["invoice", "quote", "credit_note", "recurring", "payment"];

function asMode(v: unknown): NumberDateMode {
  const s = String(v ?? "");
  return NUMBER_DATE_MODES.some((m) => m.id === s) ? (s as NumberDateMode) : "year";
}

function asPad(v: unknown, fallback = 4): number {
  const n = Number(v);
  return n >= 3 && n <= 6 ? Math.trunc(n) : fallback;
}

function asNext(v: unknown, fallback = 1): number {
  const n = Number(v);
  if (!Number.isFinite(n)) return fallback;
  const i = Math.trunc(n);
  return i < 1 ? fallback : Math.min(999999, i);
}

function asPrefix(v: unknown, fallback: string): string {
  const s = String(v ?? "")
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, "")
    .slice(0, 8);
  return v == null ? fallback : s;
}

function asBool(v: unknown, fallback: boolean): boolean {
  if (v == null) return fallback;
  if (typeof v === "boolean") return v;
  if (typeof v === "number") return v !== 0;
  if (v === "0" || v === "false") return false;
  return Boolean(v);
}

function defaults(type: NumberDocType): NumberSeries {
  return {
    prefix: type === "quote" ? "D" : type === "credit_note" ? "A" : type === "recurring" ? "R" : type === "payment" ? "P" : "F",
    date_mode: "year",
    pad: 4,
    next_number: 1,
    reset_yearly: true,
  };
}

export function formatNumberPreview(series: NumberSeries, issueDate: string): string {
  const parts: string[] = [];
  const prefix = series.prefix.replace(/[^A-Za-z0-9]/g, "").slice(0, 8).toUpperCase();
  if (prefix) parts.push(prefix);
  const ymd = /^(\d{4})-(\d{2})-(\d{2})$/.exec(issueDate);
  const y = ymd?.[1] ?? "2026";
  const m = ymd?.[2] ?? "09";
  const d = ymd?.[3] ?? "16";
  if (series.date_mode === "year" || series.date_mode === "year_month" || series.date_mode === "year_month_day") {
    parts.push(y);
  }
  if (series.date_mode === "year_month" || series.date_mode === "year_month_day") {
    parts.push(m);
  }
  if (series.date_mode === "year_month_day") {
    parts.push(d);
  }
  const n = Math.max(1, Math.trunc(series.next_number) || 1);
  const pad = asPad(series.pad);
  parts.push(String(n).padStart(pad, "0"));
  return parts.join("-");
}

export function numberingFromCompany(row: { numbering?: Partial<CompanyNumbering> | null } | null | undefined): CompanyNumbering {
  const src = row?.numbering ?? {};
  const out = {} as CompanyNumbering;
  for (const type of TYPES) {
    const d = defaults(type);
    const raw = src[type];
    const mode = asMode(raw?.date_mode);
    out[type] = {
      prefix: asPrefix(raw?.prefix, d.prefix),
      date_mode: mode,
      pad: asPad(raw?.pad, d.pad),
      next_number: asNext(raw?.next_number, d.next_number),
      reset_yearly: mode === "none" ? false : asBool(raw?.reset_yearly, d.reset_yearly),
      issued: raw?.issued === true,
    };
  }
  return out;
}
